import React, { useState } from "react";
import { Checkbox, Select } from "antd";
import { Button, Card, Col, Container, Label, Row, Spinner } from "reactstrap";
import { CustomerAndVendorHeader } from "../../components/Headers/Headers";
import BillingDetailsCard from "../../components/cards/BillingDetailsCard";
import NotesCard from "../../components/cards/NotesCard";
import { DropdownLabel } from "../../components/dropdown/DropdownLabel";
import FormInput from "../../components/formInput/FormInput";
import PageName from "../../components/react_helmet/PageName";
import LogTable from "../../components/table/LogTable";
import PurchaseOrderList from "../../components/table/PurchaseOrderList";
import { orderTerms } from "../../data";
import useVendorDetails from "./customHooks/useVendorDetails";
import CountButton from "../../components/button/CountButton";
import CustomerDetailsSkeleton from "../../components/skeleton/CustomerDetailsSkeleton";
import TableSkeleton from "../../components/skeleton/TableSkeleton";

const VendorDetails = () => {
  const {
    note,
    setNote,
    billingData,
    setBillingData,
    addVendorNotes,
    handleUpdate,
    purchaseOrdersbyVendor,
    vendorInfo,
    loading,
    notes,
    loader,
    poLoading
  } = useVendorDetails();

  return (
    <div className="settings-container pb-5">
      <PageName title={`${vendorInfo?.name ?? 'Vendor'} Details`} />
      {
        loading ? (
          <div className="mt-4">
            <CustomerDetailsSkeleton />
          </div>
        ) : (
          <Container>
            <div className="mb-4">
              <CustomerAndVendorHeader name="Vendor" ID={vendorInfo?.id} handleUpdate={handleUpdate} loader={loader} />
              <div className="text-muted d-flex gap-4 mt-2 px-1" style={{ fontSize: "0.85rem" }}>
                {vendorInfo?.createdBy?.username && (
                  <div>
                    <span className="fw-semibold">Created by:</span> {vendorInfo.createdBy.username}
                    {vendorInfo.createdAt && ` on ${new Date(vendorInfo.createdAt).toLocaleString()}`}
                  </div>
                )}
                {vendorInfo?.updatedBy?.username && (
                  <div>
                    <span className="fw-semibold">Last updated by:</span> {vendorInfo.updatedBy.username}
                    {vendorInfo.updatedAt && ` on ${new Date(vendorInfo.updatedAt).toLocaleString()}`}
                  </div>
                )}
              </div>
            </div>

            <Row className="mt-4 g-4">
              <Col lg={5}>
                <BillingDetailsCard
                  billingData={billingData}
                  setBillingData={setBillingData}
                />
              </Col>
              <Col lg={7}>
                <Card className="border-0 shadow-sm rounded-16 p-4 bg-white h-100" style={{ border: "1px solid #f1f5f9" }}>
                  <div className="d-flex align-items-center mb-4">
                    <div className="bg-primary-soft p-2 rounded-8 me-3">
                      <h3 className="primary-color mb-0 fs-18 fw-bold">Vendor Preferences</h3>
                    </div>
                  </div>

                  <div className="mb-3 p-3 bg-light-soft rounded-12 border border-dashed d-flex align-items-center">
                    <Checkbox
                      className="modern-checkbox"
                      checked={billingData?.blind_dropship ?? false}
                      onChange={(e) => {
                        setBillingData({
                          ...billingData,
                          blind_dropship: e.target.checked,
                        });
                      }}
                    >
                      <span className="ms-2 fw-600 fs-14">Enable Blind Dropshipping</span>
                    </Checkbox>
                  </div>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="mb-1 fw-600 text-muted fs-13 uppercase letter-spacing-1">Payment Terms</Label>
                        <DropdownLabel
                          customClassName={"modern-dropdown rounded-8"}
                          placeholder={"Select Terms"}
                          options={orderTerms?.map((i) => i.value)}
                          value={billingData?.terms}
                          onSelect={(e, value) =>
                            setBillingData({
                              ...billingData,
                              terms: value?.children,
                            })
                          }
                          className={"drop_down_label_style w-100"}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="mb-1 fw-600 text-muted fs-13 uppercase letter-spacing-1">Return Period</Label>
                        <FormInput
                          placeholder={"e.g. 30 Days"}
                          className={'modern-input'}
                          value={billingData?.order_return_status}
                          handleChange={(e) => {
                            setBillingData({
                              ...billingData,
                              order_return_status: e.target.value,
                            });
                          }}
                        />
                      </div>
                    </Col>
                  </Row>

                  <div className="mt-4 p-3 bg-light-soft rounded-12">
                    <Label className="mb-2 fw-600 text-dark fs-14">Internal Vendor Notes</Label>
                    <NotesCard
                      className={"modern-textarea mb-2"}
                      height={"100px"}
                      width={"100%"}
                      type={"textarea"}
                      placeholder={"Add a note about this vendor..."}
                      value={note}
                      handleChange={(e) => setNote(e.target.value)}
                    />
                    <div className="d-flex justify-content-end mt-2">
                      <Button
                        className="create-user-btn"
                        onClick={() => {
                          addVendorNotes();
                        }}
                      >
                        Add Note
                      </Button>
                    </div>
                  </div>
                </Card>

              </Col>


              <Col md={12} className="mt-5">
                <div className="settings-card shadow-sm border-0">
                  <CountButton title="Purchase Orders" count={purchaseOrdersbyVendor?.length} />

                  <div className="p-0">
                    {
                      poLoading ? (
                        <div className="mt-3">
                          <TableSkeleton columns={8} />
                        </div>
                      ) : (
                        <PurchaseOrderList purchaseOrdersbyVendor={purchaseOrdersbyVendor} />
                      )
                    }
                  </div>
                </div>
              </Col>
            </Row>

            <div className="mt-4 pb-4">
              <LogTable title="Vendor Interaction Logs" orderNotes={notes?.sort((a, b) => b?.createdAt - a?.createdAt) ?? []} />
            </div>

          </Container>
        )
      }
    </div>
  );
};

export default VendorDetails;
